import { LoadingSpinner } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Stack, useTheme } from "@mui/material";
import { useGetAgreements } from "@src/appV2/Agreements/api/useGetAgreements";
import { AgreementKind } from "@src/appV2/Agreements/types";
import { getNewestSignedAgreementFromResponse } from "@src/appV2/Agreements/utils/getNewestSignedAgreementFromResponse";
import { ErrorPageTemplate } from "@src/appV2/Errors/Template";
import { APP_V2_APP_EVENTS, ClipboardLogo } from "@src/appV2/lib";
import { useLogErrorEffect } from "@src/appV2/lib/analytics/useLogErrorEffect";
import { PdfViewerProvider } from "@src/appV2/PdfViewer/context";
import { Button } from "@src/appV2/redesign/components/Button";
import { PageContent } from "@src/appV2/redesign/WorkerShifts/components/PageContent";

import { type OnboardingStepProps } from "../OnboardingStepWrapper";
import { JobSearchAgreementStepContainer } from "./JobSearchAgreementStepContainer";

interface JobSearchAgreementStepContentProps extends OnboardingStepProps {
  isOptional?: boolean;
  onSkip?: () => void;
}

export function JobSearchAgreementStepContent(props: JobSearchAgreementStepContentProps) {
  const { worker, onNext, isOptional = false, onSkip } = props;

  const workerId = worker?.userId ?? "";
  const theme = useTheme();

  const { data: agreements, status: agreementsStatus } = useGetAgreements(
    {
      workerId,
      kind: [AgreementKind.JOB_SEARCH],
    },
    { refetchOnWindowFocus: false, enabled: Boolean(workerId) }
  );

  const isWorkerUndefined = !isDefined(worker);
  useLogErrorEffect(
    APP_V2_APP_EVENTS.GET_AGREEMENTS_FAILURE,
    {
      error: new Error("Worker is undefined"),
    },
    {
      enabled: isWorkerUndefined,
    }
  );

  if (agreementsStatus === "loading") {
    return (
      <PageContent>
        <Stack spacing={3} alignItems="center" justifyContent="center" height="100%" width="100%">
          <LoadingSpinner size="large" />
        </Stack>
      </PageContent>
    );
  }

  const agreementToSign =
    agreementsStatus === "success" ? getNewestSignedAgreementFromResponse(agreements) : undefined;

  const pdfUrl = agreementToSign?.attributes.downloadUrl;

  if (isWorkerUndefined || !isDefined(agreementToSign) || agreementsStatus === "error") {
    return (
      <ErrorPageTemplate
        illustration={<ClipboardLogo />}
        title="Oops! Something went wrong."
        description="We're sorry, but something went wrong. Please try again or contact support if the problem persists."
        sx={{
          backgroundColor: theme.palette.background.tertiary,
        }}
        actions={
          <Stack width="100%">
            <Button
              fullWidth
              variant="contained"
              size="large"
              onClick={() => {
                globalThis.location.reload();
              }}
            >
              Reload page
            </Button>
          </Stack>
        }
      />
    );
  }

  return (
    <PdfViewerProvider fileName="job_search_agreement.pdf" pdfUrl={pdfUrl}>
      <JobSearchAgreementStepContainer
        agreementsStatus={agreementsStatus}
        agreementToSign={agreementToSign}
        isOptional={isOptional}
        onAcceptAgreement={onNext}
        onSkip={onSkip}
      />
    </PdfViewerProvider>
  );
}
